import { useRef } from 'react';
import type {
  IframePoolItem,
  IframePoolOfferOdds,
  JustPulledItem,
  PoolOfferDetail,
  PoolOfferListItem,
  TierBadge,
} from '../../../modules/api';
import { JustPulled } from '../../../modules/justPulled';
import { PotentialHits } from '../../../modules/potentialHits';
import { Cards } from '../../../modules/design-system';
import type { CategoryPillItem } from '../../../modules/design-system';
import { BottomAction } from './BottomAction';
import { isOfferAvailable } from './offerStatus';
import { OddsPanel, OddsPanelSkeleton } from './OddsPanel';
import { PackInfoCard } from './PackInfoCard';
import { Slider } from './Slider';

/**
 * Grid layout for the modern pack-detail page.
 *
 * Mirrors the classic grid exactly (same container breakpoints, same slot
 * order) so future iterations only need to swap stub blocks for real
 * components — no surrounding-layout churn.
 *
 *   < 900px  → single column, `action` block at the end of the flow
 *   ≥ 900px  → two columns (hero | 360px sidebar); `action` moves into
 *              the sidebar.
 */

export interface PackLayoutProps {
  /** Currently-centered offer's detail — drives info card, odds, RIP price. */
  offer: PoolOfferDetail | null;
  /** Every pack in the active category — drives the slider slots. */
  categoryOffers: PoolOfferListItem[];
  /** Active offer id (slider centre). */
  activeOfferId: number | null;
  odds: IframePoolOfferOdds | null;
  maxForPurchaseCount: number | undefined;
  potentialHits: IframePoolItem[];
  potentialHitsLoading: boolean;
  justPulledItems: JustPulledItem[];
  justPulledLoading: boolean;
  onShowJustPulled?: () => void;
  categories: CategoryPillItem[];
  activeCategoryId?: string;
  /** Header pill click — updates URL + active gameId. */
  onCategorySelect: (id: string) => void;
  /** Slider swipe — updates active offer in-page, keeps URL stable. */
  onSlideChange?: (offerId: number) => void;
  onTierSelect?: (poolOfferId: number) => void;
  quantity?: number;
  onQuantityChange?: (next: number) => void;
  onBuy?: () => void;
  unitPrice?: number | string;
  /**
   * Operator-side currency snapshot (e.g. `"EUR"`) — surfaced from the
   * current offer (`PoolOfferDetail`). When set, the BottomAction shows
   * the casino-currency total instead of USD. `null` keeps the USD label.
   */
  operatorCurrency?: string | null;
  /** USD-per-1-operator-currency-unit rate. See PoolOfferDetail. */
  exchangeRate?: number | null;
}

export function PackLayout({
  offer,
  categoryOffers,
  activeOfferId,
  odds,
  maxForPurchaseCount,
  potentialHits,
  potentialHitsLoading,
  justPulledItems,
  justPulledLoading,
  onShowJustPulled,
  categories: _categories,
  activeCategoryId: _activeCategoryId,
  onCategorySelect: _onCategorySelect,
  onSlideChange,
  quantity = 1,
  onQuantityChange,
  onBuy,
  unitPrice = 0,
  operatorCurrency = null,
  exchangeRate = null,
}: PackLayoutProps) {
  const actionVisible = !offer || isOfferAvailable(offer.status);
  // Mobile "Potential Hits" header scrolls down to the actual section below.
  const potentialHitsRef = useRef<HTMLDivElement>(null);

  // Convert category-offers list to TierBadge[] so PackInfoCard's tier
  // selector can render one card per sibling pack. Clicking a card
  // fires `onSlideChange` (same handler the slider's swipe uses) so the
  // slider animates to that offer — and PackInfoCard, OddsPanel,
  // BottomAction all follow because they read from `activeOfferId`.
  //
  // Tier label = badge name (e.g. "Starter" / "Premier" / "Pro") when
  // the backend has configured one for this pool_offer; otherwise we
  // fall back to the pack title so the card never shows blank.
  //
  // Each offer's ShortSerializer-attached `suitable_badges` lists every
  // sibling's badge with `is_self=true` flagging the badge that
  // represents this offer. Some dev offers don't have any Badge row
  // pointing to their pool_offer_id — in that case `ownBadge` is
  // undefined and we use the pack title.
  const categoryOffersAsTiers: TierBadge[] = categoryOffers.map((o) => {
    const ownBadge =
      o.suitableBadges.find((b) => b.poolOfferId === o.id) ??
      o.suitableBadges.find((b) => b.isSelf);
    return {
      poolOfferId: o.id,
      name: ownBadge?.name?.trim() || o.title,
      price: o.price,
      imageUrl: o.packImageUrl,
      subImageUrl: null,
      isSelf: activeOfferId !== null && o.id === activeOfferId,
    };
  });

  // Slider needs at least the current pack to render. When the category
  // index endpoint returns 0 offers (e.g. operator-scope filter excludes
  // siblings, or the URL `game_id` doesn't sit in any visible category),
  // synthesise a single-item list from `offer` so the centre pack still
  // shows. Slider already gates chevrons/dots/drag on `len > 1`, so the
  // synthesised single-item case renders as a static centred pack — no
  // controls, no peeks.
  const sliderOffers: PoolOfferListItem[] =
    categoryOffers.length > 0
      ? categoryOffers
      : offer
        ? [
            {
              id: offer.id,
              title: offer.title,
              price: offer.price,
              status: offer.status,
              isMultiple: false,
              packImageUrl: offer.packImageUrl,
              packLogoImageUrl: offer.packLogoImageUrl,
              pedestalImageUrl: offer.pedestalImageUrl,
              suitableBadges: offer.suitableBadges,
              operatorCurrency: offer.operatorCurrency,
              exchangeRate: offer.exchangeRate,
            },
          ]
        : [];

  return (
    // `@container` lives on a wrapper with NO padding so its content-box
    // size (which is what `@container size` queries against) matches the
    // viewport. If we put `px-X` on the same element, the padding
    // shrinks the content-box and rules like
    // `@[900px]:grid-cols-...` stop firing until viewport ≈ 964 (900 +
    // 64 of padding) — a Tailwind 4 / CSS Container-Query gotcha.
    <div className="@container">
      <div
        className="
          pb-8 @[600px]:px-6 @[900px]:px-8
          mx-auto max-w-350
          grid grid-cols-1 gap-2
          @[900px]:grid-cols-[minmax(0,1fr)_360px]
          @[900px]:gap-x-6 @[900px]:gap-y-2
          @[1200px]:grid-cols-[minmax(0,1fr)_400px]
        "
      >
        {/* HERO / SLIDER — left column, row 1.
            On desktop the slider bleeds left by the PackLayout's `px-8`
            so it runs from the viewport's left edge to the sidebar gap,
            matching Figma 543:2118 where the slider area starts flush
            against the frame's left edge. Width is grown by the same
            amount so the right edge stays where the grid put it. */}
        <div
          className="
            relative @[900px]:col-start-1 @[900px]:row-start-1
            @[900px]:-ml-8 @[900px]:w-[calc(100%+2rem)]
          "
        >
          <Slider
            offers={sliderOffers}
            activeOfferId={activeOfferId}
            {...(onSlideChange ? { onSlideChange } : {})}
          />
        </div>

        {/* MOBILE-ONLY PotentialHits header (Figma 629:588).
            Replaces the slider's own dot strip on mobile — sits between
            the slider and the OddsPanel, ABOVE the sidebar block. Dots
            reflect `activeOfferId` (one per offer in the current
            category). Tapping a dot fires the same `onSlideChange` the
            slider's swipe / tier-card click uses, so all three stay in
            sync. */}
        {/* `px-5` (= 20 px) on the header matches the inner `left-5
            right-5` positioning of OddsPanel's "Min Value" / "Big Win
            Chance" text — so the label here lines up vertically with
            the leftmost column of OddsPanel and the dots end on the
            same column as "Big Win Chance". */}
        <div className="@[900px]:hidden flex items-center justify-between gap-3 px-5 pt-2 pb-3">
          {/* Label + stacked-cards icon (Figma 702:4741). Tapping it
              smooth-scrolls down to the Potential Hits section below. */}
          <button
            type="button"
            onClick={() =>
              potentialHitsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
            aria-label="Scroll to Potential Hits"
            className="flex items-center gap-[7px] cursor-pointer text-white"
          >
            <span className="text-[15px] leading-[23px] font-semibold whitespace-nowrap">
              Potential Hits
            </span>
            <Cards size={22} aria-hidden className="shrink-0 opacity-50" />
          </button>
          {categoryOffers.length > 1 && (
            <div
              data-co-component="SliderDots"
              className="flex items-center gap-[7px]"
            >
              {categoryOffers.map((o) => {
                const active = activeOfferId !== null && o.id === activeOfferId;
                return (
                  <button
                    key={o.id}
                    type="button"
                    aria-label={`Go to ${o.title}`}
                    aria-current={active || undefined}
                    onClick={() => onSlideChange?.(o.id)}
                    className={
                      'rounded-full transition-all cursor-pointer ' +
                      (active ? 'h-2 w-2' : 'h-[6px] w-[6px]')
                    }
                    // Colour flows through `--co-SliderDots-{active,idle}` so
                    // the Design Constructor (SliderDots component) recolours
                    // every dot at once. Fallbacks reproduce the original
                    // `bg-white` / `bg-white/40` Tailwind tones — visual
                    // parity for unmodified deploys.
                    style={{
                      background: active
                        ? 'var(--co-SliderDots-active, rgba(255,255,255,1))'
                        : 'var(--co-SliderDots-idle, rgba(255,255,255,0.4))',
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* SIDEBAR — right column. On desktop a peek pack from the
            slider may slide *behind* the sidebar (no hard clip at the
            slider edge), so the sidebar is given a higher z-index and
            `relative` to overlay any peek silhouette that bleeds into
            its column. The cards themselves are GLASS_SURFACE-blurred,
            so the peek reads as a soft shape under the sidebar. */}
        <aside
          className="
            flex flex-col gap-2
            @[900px]:col-start-2 @[900px]:row-start-1
            @[900px]:relative @[900px]:z-30
          "
        >
          <PackInfoCard
            title={offer?.title ?? ''}
            tiers={categoryOffersAsTiers}
            packLogoImageUrl={offer?.packLogoImageUrl ?? null}
            {...(onSlideChange ? { onTierSelect: onSlideChange } : {})}
          />

          {odds ? (
            <OddsPanel
              minValue={odds.minValue}
              maxPull={odds.estimatedValue}
              bigWinChance={odds.bigWinChance}
            />
          ) : (
            <OddsPanelSkeleton />
          )}

          {actionVisible && (
            <div className="hidden @[900px]:block">
              <BottomAction
                quantity={quantity}
                onQuantityChange={onQuantityChange ?? (() => {})}
                onBuy={onBuy ?? (() => {})}
                unitPrice={unitPrice}
                operatorCurrency={operatorCurrency}
                exchangeRate={exchangeRate}
                {...(maxForPurchaseCount !== undefined ? { maxQty: maxForPurchaseCount } : {})}
              />
            </div>
          )}
        </aside>

        {/* DESKTOP-ONLY JustPulled — Figma 543:2216. Glass panel with
            header + marquee of recent pulls, scoped to the currently
            active pack. Shared with classic — the visual treatment is
            identical, so we reuse the same component instead of
            duplicating it under `templates/modern`. */}
        <div className="hidden @[900px]:block @[900px]:col-start-1 @[900px]:row-start-2">
          <JustPulled
            items={justPulledItems}
            loading={justPulledLoading}
            {...(onShowJustPulled ? { onShowAll: onShowJustPulled } : {})}
          />
        </div>

        {/* PotentialHits — visible on every breakpoint, matches classic.
            Mobile flow renders it after the slider / OddsPanel; desktop
            grid pins it to left column row 3. Reuses the shared
            component (visual identical between classic and modern). */}
        <div
          ref={potentialHitsRef}
          className="px-4 @[900px]:px-0 @[900px]:col-start-1 @[900px]:row-start-3 scroll-mt-3"
        >
          <PotentialHits
            items={potentialHits}
            loading={potentialHitsLoading}
          />
        </div>

        {/* Mobile spacer — reserves 160px under the in-flow content so it
            never slides under the portal'd `MobileBar` (which lives in
            `<body>` and is fixed to the viewport bottom). The bar itself
            is rendered exactly once from the desktop `<BottomAction>`
            mount inside the sidebar — its `MobileBar` child portals to
            body globally, so a second mount here would create a
            duplicate rail in `<body>`. */}
        {actionVisible && (
          <div className="@[900px]:hidden h-[160px]" aria-hidden />
        )}
      </div>
    </div>
  );
}
